import { useState } from 'react'
import { useNavigate } from '../nav'
import { Bug as BugIcon, ChevronDown, ChevronRight, GitBranch, Scale, XCircle } from 'lucide-react'
import StatusBadge from '../StatusBadge'
import { stepScreenshotUrl, type ChronologyStep } from '../../data/projectTypes'
import './BugsFoundList.css'

interface JudgeResult {
  approved: boolean
  assessment?: string
  problems: string[]
  judge_type?: 'evidence' | 'requirements'
}

interface JudgeSubmission {
  attempt: number
  submitted_at: string
  submitted_data?: {
    title?: string
    description?: string | null
    actual_behavior?: string | null
    analysis?: Record<string, unknown> | null
  }
  result: JudgeResult
  evidence_result?: JudgeResult
  requirements_result?: JudgeResult
}

export interface BugItem {
  id: string
  severity: string
  title: string
  status: string
  analysis?: unknown
  judge_submissions?: JudgeSubmission[] | null
  // True when this row is a rediscovery of an already-filed bug (the run matched an existing bug
  // rather than creating a new one). The bug it links to is the canonical existing bug.
  is_duplicate?: boolean
}

interface BugsFoundListProps {
  bugs: BugItem[]
  projectId: string
  heading?: string
}

function JudgePhaseResult({ label, result }: { label: string; result: JudgeResult }) {
  return (
    <div className="bugs-found-list__judge-phase">
      <div className="bugs-found-list__judge-phase-header">
        <span className={`bugs-found-list__judge-phase-badge ${result.approved ? 'bugs-found-list__judge-phase-badge--passed' : 'bugs-found-list__judge-phase-badge--failed'}`}>
          {label}: {result.approved ? 'Passed' : 'Failed'}
        </span>
      </div>
      {result.problems.length > 0 && (
        <ul className="bugs-found-list__judge-problems">
          {result.problems.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      )}
      {result.assessment && (
        <p className="bugs-found-list__judge-assessment">{result.assessment}</p>
      )}
    </div>
  )
}

function JudgeSubmissionDetail({ sub }: { sub: JudgeSubmission }) {
  const [showAnalysis, setShowAnalysis] = useState(false)
  const result = sub.result ?? { approved: false, problems: [] }
  const analysis = sub.submitted_data?.analysis as Record<string, unknown> | null | undefined
  const hasPhaseResults = !!sub.evidence_result || !!sub.requirements_result

  return (
    <div className="bugs-found-list__judge-attempt">
      <div className="bugs-found-list__judge-attempt-header">
        <XCircle size={12} aria-hidden="true" />
        <span>Attempt {sub.attempt}</span>
        <time>{new Date(sub.submitted_at).toLocaleString()}</time>
      </div>
      {hasPhaseResults ? (
        <>
          {sub.evidence_result && <JudgePhaseResult label="Evidence" result={sub.evidence_result} />}
          {sub.requirements_result && <JudgePhaseResult label="Requirements" result={sub.requirements_result} />}
        </>
      ) : (
        <>
          {result.problems.length > 0 && (
            <ul className="bugs-found-list__judge-problems">
              {result.problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          )}
          {result.assessment && (
            <p className="bugs-found-list__judge-assessment">{result.assessment}</p>
          )}
        </>
      )}
      {analysis && (
        <div className="bugs-found-list__judge-analysis">
          <button
            type="button"
            className="bugs-found-list__judge-analysis-toggle"
            onClick={(e) => { e.stopPropagation(); setShowAnalysis(!showAnalysis) }}
            data-analytics-event="bug-judge-analysis-toggle"
          >
            {showAnalysis ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Submitted Analysis
          </button>
          {showAnalysis && (
            <div className="bugs-found-list__judge-analysis-content">
              {!!sub.submitted_data?.actual_behavior && (
                <div className="bugs-found-list__judge-field">
                  <span className="bugs-found-list__judge-field-label">Actual Behavior</span>
                  <p>{sub.submitted_data.actual_behavior}</p>
                </div>
              )}
              {!!(analysis.root_cause && typeof analysis.root_cause === 'object' && (analysis.root_cause as Record<string, unknown>).text) && (
                <div className="bugs-found-list__judge-field">
                  <span className="bugs-found-list__judge-field-label">Root Cause</span>
                  <p>{String((analysis.root_cause as Record<string, unknown>).text)}</p>
                </div>
              )}
              {Array.isArray(analysis.chronology) && analysis.chronology.length > 0 && (
                <div className="bugs-found-list__judge-field">
                  <span className="bugs-found-list__judge-field-label">Chronology ({analysis.chronology.length} steps)</span>
                  <ol className="bugs-found-list__judge-chronology">
                    {(analysis.chronology as ChronologyStep[]).map((step, i) => {
                      const frame = stepScreenshotUrl(step)
                      return (
                        <li key={i}>
                          {step.text ?? '(no description)'}
                          {frame && (
                            <img
                              src={frame}
                              alt={`Step ${i + 1}`}
                              className="bugs-found-list__judge-screenshot"
                              loading="lazy"
                            />
                          )}
                        </li>
                      )
                    })}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function BugJudgeDetails({ bug }: { bug: BugItem }) {
  const [expanded, setExpanded] = useState(false)
  const submissions = bug.judge_submissions ?? []
  if (submissions.length === 0) return null

  const allProblems = submissions.flatMap(s => s.result?.problems ?? [])
  const uniqueProblems = [...new Set(allProblems)]

  return (
    <div className="bugs-found-list__judge" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="bugs-found-list__judge-toggle"
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
        data-analytics-event="bug-judge-toggle"
      >
        <Scale size={12} aria-hidden="true" />
        <span>{submissions.length} judge attempt{submissions.length > 1 ? 's' : ''}</span>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {!expanded && uniqueProblems.length > 0 && (
        <div className="bugs-found-list__judge-summary">
          {uniqueProblems.slice(0, 2).map((p, i) => (
            <span key={i} className="bugs-found-list__judge-problem-chip">{p}</span>
          ))}
          {uniqueProblems.length > 2 && (
            <span className="bugs-found-list__judge-problem-chip">+{uniqueProblems.length - 2} more</span>
          )}
        </div>
      )}
      {expanded && (
        <div className="bugs-found-list__judge-details">
          {submissions.map((sub) => (
            <JudgeSubmissionDetail key={sub.attempt} sub={sub} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function BugsFoundList({ bugs, projectId, heading }: BugsFoundListProps) {
  const navigate = useNavigate()
  const label = heading ?? 'Bugs Found'

  if (bugs.length === 0) return null

  return (
    <section className="bugs-found-list" data-testid="bugs-found-list" aria-labelledby="bugs-found-heading">
      <h2 className="bugs-found-list__header" id="bugs-found-heading"><BugIcon size={14} aria-hidden="true" /> {label} ({bugs.length})</h2>
      <ul className="bugs-found-list__items">
        {bugs.map(bug => (
          <li key={bug.id}>
            <button
              className="bugs-found-list__item"
              onClick={() => navigate(`/projects/${projectId}/bugs/${bug.id}`)}
              data-testid={`bug-item-${bug.id}`}
              data-analytics-event="bug-row-open"
            >
              <StatusBadge status={bug.severity} variant="severity" />
              <span className="bugs-found-list__title">{bug.title}</span>
              {bug.is_duplicate && (
                <span className="bugs-found-list__duplicate-badge" title="Rediscovery of an existing bug">
                  <GitBranch size={11} aria-hidden="true" /> Duplicate
                </span>
              )}
              <StatusBadge status={bug.status} variant="bug" />
            </button>
            {bug.status === 'invalid' && bug.judge_submissions && bug.judge_submissions.length > 0 && (
              <BugJudgeDetails bug={bug} />
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
